/**
 * /admin/settings — platform-admin-only page for inline-agent runtime
 * settings: which Anthropic model the "edit with Kin" agent runs, the
 * effort level, and rotation of the Anthropic API key. Changes apply
 * on the next agent message — no redeploy.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, apiMessage } from '../../api/client.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconShield } from '../../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Field,
  Mono,
  PageHeader,
  PageSkeleton,
  inputClass,
} from '../../components/ui.js';
import { relTime } from '../../lib/format.js';
import { runAction, type ActionMessage } from '../../lib/runAction.js';

type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

interface AgentSettingsResponse {
  model: string;
  effort: Effort;
  has_api_key: boolean;
  updated_at: number | null;
  updated_by: string | null;
  defaults: { model: string; effort: Effort };
}

interface ModelOption {
  id: string;
  display_name: string;
  created_at: string;
}

const EFFORT_OPTIONS: { value: Effort; label: string; description: string }[] = [
  { value: 'low', label: 'low', description: 'Fastest and cheapest — simple, scoped edits.' },
  { value: 'medium', label: 'medium', description: 'Cost-saving step-down from the default.' },
  {
    value: 'high',
    label: 'high',
    description: 'The API default — balanced quality vs. tokens (platform default).',
  },
  {
    value: 'xhigh',
    label: 'xhigh',
    description: 'Deepest practical reasoning for hard coding work; noticeably more tokens.',
  },
  {
    value: 'max',
    label: 'max',
    description: 'Maximum reasoning — slowest and most expensive; can overthink simple tasks.',
  },
];

export function AdminSettings() {
  const [settings, setSettings] = useState<AgentSettingsResponse | null>(null);
  const [models, setModels] = useState<ModelOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ActionMessage | null>(null);

  const [model, setModel] = useState('');
  const [effort, setEffort] = useState<Effort>('high');
  const [apiKey, setApiKey] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await api.get<AgentSettingsResponse>('/api/admin/settings/agent');
      setSettings(fresh);
      setModel(fresh.model);
      setEffort(fresh.effort);
      setError(null);
      // Live model listing needs a stored key; a 409 just means "type
      // the model id by hand" — not an error worth a banner.
      if (fresh.has_api_key) {
        try {
          const list = await api.get<{ models: ModelOption[] }>('/api/admin/settings/agent/models');
          setModels(list.models);
        } catch {
          setModels(null);
        }
      } else {
        setModels(null);
      }
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function save() {
    const keyChanged = apiKey.trim().length > 0;
    if (keyChanged && !apiKey.trim().startsWith('sk-ant-')) {
      setMessage({ tone: 'error', text: 'Anthropic API keys start with "sk-ant-".' });
      return;
    }
    if (model.trim().length === 0) {
      setMessage({ tone: 'error', text: 'Pick or type a model id.' });
      return;
    }
    await runAction({
      do: () =>
        api.put<AgentSettingsResponse>('/api/admin/settings/agent', {
          model: model.trim(),
          effort,
          ...(keyChanged ? { api_key: apiKey.trim() } : {}),
        }),
      onSuccess: () => {
        setApiKey('');
        return 'Saved — the next agent message uses these settings.';
      },
      errorPrefix: "Couldn't save",
      setBusy,
      setMessage,
      reload,
    });
  }

  if (loading && !settings) return <PageSkeleton rows={4} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Settings"
        description="Runtime settings for the inline app-editing agent. Changes apply on the next agent message — no redeploy."
      />

      <AdminTabs />

      {error && <Banner tone="error">{error}</Banner>}
      {message && (
        <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
          {message.text}
        </Banner>
      )}

      <Card>
        <CardHeader
          title="Inline agent"
          description="Model and effort for every 'edit with Kin' chat turn, platform-wide."
        />
        <div className="px-5 py-4 space-y-5">
          <Field label="Model">
            <input
              list="agent-model-options"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={settings?.defaults.model}
              autoComplete="off"
              className={`${inputClass} font-mono`}
            />
            <datalist id="agent-model-options">
              {(models ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name}
                </option>
              ))}
            </datalist>
            <p className="text-xs text-muted mt-1">
              {models
                ? 'Suggestions come live from Anthropic’s Models API, so newly released models appear here automatically.'
                : 'Save an Anthropic API key below to get live model suggestions; until then, type the model id (e.g. claude-sonnet-5).'}{' '}
              Platform default: <Mono>{settings?.defaults.model}</Mono>.
            </p>
          </Field>

          <Field label="Effort">
            <select
              value={effort}
              onChange={(e) => setEffort(e.target.value as Effort)}
              className={inputClass}
            >
              {EFFORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">
              {EFFORT_OPTIONS.find((o) => o.value === effort)?.description}
            </p>
          </Field>

          <Field
            label={
              <span className="inline-flex items-center gap-2">
                Anthropic API key
                {settings?.has_api_key && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand-soft text-brand font-medium">
                    ✓ on file
                  </span>
                )}
              </span>
            }
          >
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                settings?.has_api_key
                  ? 'Leave blank to keep the current key; paste to rotate'
                  : 'sk-ant-… (blank = keep using the deployed worker secret)'
              }
              autoComplete="off"
              className={`${inputClass} font-mono`}
            />
            <p className="text-xs text-muted mt-1">
              <span className="inline-flex items-center gap-1.5">
                <IconShield size={13} />
                Encrypted at rest under the platform master key; never travels back to the browser.
                Takes effect on the next agent turn — no Cloudflare secret rotation needed.
              </span>
            </p>
          </Field>

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="md"
              loading={busy}
              disabled={busy}
              onClick={() => void save()}
            >
              Save settings
            </Button>
          </div>
        </div>

        {settings && (
          <div className="border-t border-line px-5 py-4 grid sm:grid-cols-2 gap-3 text-sm">
            <StatusRow label="Active model" value={settings.model} />
            <StatusRow label="Effort" value={settings.effort} />
            <StatusRow
              label="API key"
              value={settings.has_api_key ? 'rotated via portal' : 'worker secret'}
            />
            <StatusRow
              label="Last updated"
              value={settings.updated_at ? relTime(settings.updated_at) : 'never (defaults)'}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted text-xs uppercase tracking-wider">{label}</span>
      <Mono className="text-sm">{value}</Mono>
    </div>
  );
}
